import {
  AfterContentInit,
  afterNextRender,
  Component,
  ContentChild,
  ContentChildren,
  EventEmitter,
  inject,
  Inject,
  Injector,
  Input,
  OnDestroy,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  DOCUMENT,
  Renderer2,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ModalDismissReasons, NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { getKeyCode, KEY_CODES } from '@backbase/ui-ang/util';
import { DomAttributesService } from '@backbase/ui-ang/services';
import { ModalHeaderComponent } from './modal-templates/modal-header.component';

import { ModalBodyComponent } from './modal-templates/modal-body.component';
import { BehaviorSubject, Subject, debounceTime, filter, merge, takeUntil, tap } from 'rxjs';
import { BB_MONEY_PROTECTION_BANNER_CONFIG_TOKEN } from '@backbase/ui-ang/money-protection';
import { ModalFocusService } from './modal-focus.service';

const NGB_AUTOFOCUS_SELECTOR = '[ngbAutofocus]';
const CDK_FOCUS_INITIAL_SELECTOR = '[cdkFocusInitial], [cdk-focus-initial]';

/**
 * Component that displays a modal window with comprehensive features and accessibility support.
 *
 * @remarks
 * Provides modal functionality with customizable options, money protection banner support,
 * and automatic accessibility features. Handles modal lifecycle, keyboard interactions,
 * and integrates with ng-bootstrap modal service for robust modal management.
 *
 * ### Controlling components with overflow/z-index
 * If you have nested modals, dropdowns or select components,
 * add (or remove) the property `container="body"` on your topmost component.
 * You may have to toggle around depending on your setup or if your modal/dropdown/select has overflow.
 *
 * Alternatively, you can also add the `.modal-overflow` class by doing:
 * `[modalOptions]="{ modalDialogClass: 'modal-overflow' }"`.
 *
 * ### Initial focus
 * When the modal opens, focus moves to the first match of, in order: an element marked
 * `[ngbAutofocus]`, an element marked `[cdkFocusInitial]`, then the first focusable element in the
 * modal. Mark the intended control when the first focusable element is not the right target, for
 * example when the body starts with a tooltip icon that opens on focus:
 *
 * ```html
 * <button bbButton cdkFocusInitial (click)="cancel()">Cancel</button>
 * ```
 *
 * @example
 *
 * ```html
 * <bb-modal-ui [isOpen]="showModal" (cancel)="closeModal()" (confirm)="onConfirm()">
 *   <bb-modal-header-ui title="Cookies on Backbase"></bb-modal-header-ui>
 *   <bb-modal-body-ui>
 *     This website uses cookies to enhance your browsing experience.
 *   </bb-modal-body-ui>
 *   <bb-modal-footer-ui confirmText="Accept" (confirm)="saveAccept()">
 *   </bb-modal-footer-ui>
 * </bb-modal-ui>
 * ```
 */
@Component({
  selector: 'bb-modal-ui',
  templateUrl: './modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalComponent implements OnDestroy, AfterContentInit {
  private readonly modalFocusService: ModalFocusService = inject(ModalFocusService);
  private readonly injector = inject(Injector);

  constructor(
    private readonly modalService: NgbModal,
    private readonly renderer: Renderer2,
    private readonly domAttrService: DomAttributesService,
    // cannot use type `Document` due to compilation issues caused by `strictMetadataEmit`
    @Inject(DOCUMENT) private readonly document: any,
  ) {}

  /**
   * Reference to the opened modal instance for lifecycle management.
   */
  private modalRef: NgbModalRef | undefined;

  /**
   * Unique identifier for the modal component instance.
   */
  readonly id = this.domAttrService.generateId();

  /**
   * Internal BehaviorSubject to manage modal open state.
   */
  private readonly _isOpen = new BehaviorSubject(false);
  /**
   * The flag to show the dialog window. Defaults to 'false'.
   *
   * @example
   *
   * ```html
   * <bb-modal-ui [isOpen]="showModal">
   * </bb-modal-ui>
   * ```
   */
  @Input()
  set isOpen(value: boolean) {
    this._isOpen.next(value);
  }

  /**
   * Gets the current open state of the modal.
   */
  get isOpen(): boolean {
    return this._isOpen.getValue();
  }
  /**
   * Set `aria-labelledby` with an element id that contains a brief label about the modal.
   * By default is takes the modal heading.
   *
   * @example
   *
   * ```html
   * <bb-modal-ui [isOpen]="showModal" aria-labelledby="modal-title-id">
   *   <bb-modal-header-ui id="modal-title-id" title="Confirmation"></bb-modal-header-ui>
   * </bb-modal-ui>
   * ```
   */
  @Input('aria-labelledby')
  get ariaLabelledby(): string | undefined {
    return this._ariaLabelledby ?? this.modalOptions.ariaLabelledBy ?? this.modalHeader?.headerId;
  }

  /**
   * Sets the aria-labelledby value.
   */
  set ariaLabelledby(value: string | undefined) {
    this._ariaLabelledby = value;
  }

  private _ariaLabelledby: string | undefined;

  /**
   * Set `aria-describedby` with an element id that contains a detailed description about the modal.
   *
   * @example
   *
   * ```html
   * <bb-modal-ui [isOpen]="showModal" aria-describedby="modal-description">
   *   <bb-modal-body-ui id="modal-description">
   *     This modal contains important information.
   *   </bb-modal-body-ui>
   * </bb-modal-ui>
   * ```
   */
  @Input('aria-describedby')
  get ariaDescribedby(): string | undefined {
    return this._ariaDescribedby ?? this.modalOptions.ariaDescribedBy ?? this.modalBody?.bodyId;
  }

  /**
   * Sets the aria-describedby value.
   */
  set ariaDescribedby(value: string | undefined) {
    this._ariaDescribedby = value;
  }

  private _ariaDescribedby: string | undefined;

  /**
   * Customize the ARIA role this component.
   *
   * This can be used to improve accessibility for components, for example by configuring `[role]="'alertdialog'"`
   * if it requires immediate attention of the user.
   *
   * Defaults to 'dialog'.
   *
   * @example
   *
   * ```html
   * <bb-modal-ui [role]="'alertdialog'">
   * </bb-modal-ui>
   * ```
   */
  @Input() role = 'dialog';
  /**
   * Dialog options inherited from https://ng-bootstrap.github.io/#/components/modal/api#NgbModalOptions. Defaults to an empty object.
   *
   * @example
   *
   * ```html
   * <bb-modal-ui [modalOptions]="{ backdrop: 'static' }">
   * </bb-modal-ui>
   * ```
   */
  @Input() modalOptions: NgbModalOptions = {};

  /**
   * The flag to show money protection insurance banner on top of the modal.
   * In order to see banner you will need to configure `BB_MONEY_PROTECTION_BANNER_CONFIG_TOKEN` to provide
   * component to render in banner content outlet.
   *
   * @example
   *
   * ```html
   * <bb-modal-ui [isOpen]="showModal" [showMoneyProtectionBanner]="true">
   *   <bb-modal-header-ui title="Payment"></bb-modal-header-ui>
   *   <bb-modal-body-ui>Payment details</bb-modal-body-ui>
   * </bb-modal-ui>
   * ```
   */
  @Input() showMoneyProtectionBanner = false;
  /**
   * Custom class to be assigned for money protection banner inside modal
   * @type {string}
   *
   * @example
   *
   * ```html
   * <bb-modal-ui
   *   [showMoneyProtectionBanner]="true"
   *   moneyProtectionBannerClasses="custom-banner-style">
   * </bb-modal-ui>
   * ```
   */
  @Input() moneyProtectionBannerClasses = '';
  /**
   * The event that's fired after confirm button is pressed. Can be used with
   * (click)="dialogRef.onConfirm()" where dialogRef is a templateRef on <bb-dialog-ui #dialogRef />
   *
   * @example
   *
   * ```html
   * <bb-modal-ui [isOpen]="showModal" (confirm)="onConfirm()">
   *   <bb-modal-footer-ui confirmText="Save" (confirm)="onConfirm()">
   *   </bb-modal-footer-ui>
   * </bb-modal-ui>
   * ```
   */
  @Output() confirm = new EventEmitter();
  /**
   * The event that's fired after cancel button is pressed. Can be used with
   * (click)="dialogRef.onCancel()" where dialogRef is a templateRef on <bb-dialog-ui #dialogRef />
   *
   * @example
   *
   * ```html
   * <bb-modal-ui [isOpen]="showModal" (cancel)="closeModal()">
   *   <bb-modal-footer-ui cancelText="Cancel" (cancel)="closeModal()">
   *   </bb-modal-footer-ui>
   * </bb-modal-ui>
   * ```
   */
  @Output() cancel = new EventEmitter();
  /**
   * Used for two way binding with the isOpen Input. If used, backdrop
   * and esc dismiss cases will be handled automatically.
   *
   * @example
   *
   * ```html
   * <bb-modal-ui [(isOpen)]="showModal">
   *   <bb-modal-header-ui title="Two-way Binding"></bb-modal-header-ui>
   * </bb-modal-ui>
   * ```
   */
  @Output() isOpenChange = new EventEmitter();

  @ViewChild('modalContent', { static: true }) modalContent: TemplateRef<any> | undefined;

  @ContentChild(ModalHeaderComponent) modalHeader: ModalHeaderComponent | undefined;
  @ContentChildren(ModalHeaderComponent) private modalHeaders: QueryList<ModalHeaderComponent> | undefined;
  @ContentChild(ModalBodyComponent) modalBody: ModalBodyComponent | undefined;

  /**
   * @internal
   */
  protected readonly moneyProtectionBanner? = inject(BB_MONEY_PROTECTION_BANNER_CONFIG_TOKEN, { optional: true });

  private emitConfirm = true;
  private headerChange$ = new Subject<void>();
  private destroy$ = new Subject<void>();
  private registeredModalWindow: HTMLElement | null = null;

  ngAfterContentInit(): void {
    this.subscribeIsOpen();
    this.subscribeHeaderClose();
    this.modalHeaders?.changes
      .pipe(
        tap(() => {
          this.headerChange$.next();
        }),
        debounceTime(0),
        filter(Boolean),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.subscribeHeaderClose();
      });
  }

  /**
   * Handles modal dismiss reasons and emits isOpenChange event.
   *
   * @remarks
   * Used internally to manage modal dismiss behavior and notify parent components
   * of state changes. Automatically emits false to trigger two-way binding updates.
   *
   * @param reason - The reason for dismissing the modal
   * @returns The same dismiss reason passed in
   */
  getDismissReason(reason: ModalDismissReasons | string): ModalDismissReasons | string {
    this.isOpenChange.emit(false);

    return reason;
  }

  /**
   * Dismisses the modal with an optional reason.
   *
   * @remarks
   * Programmatically dismisses the modal, typically used for cancel operations
   * or when the modal should be closed without confirming any actions.
   *
   * @param reason - Optional reason for dismissing the modal
   */
  dismissModal(reason?: string): void {
    if (this.modalRef) this.modalRef.dismiss(reason);
  }

  /**
   * Closes the modal with an optional result.
   *
   * @remarks
   * Programmatically closes the modal, typically used for confirm operations
   * or when the modal should be closed with a successful result.
   *
   * @param reason - Optional result or reason for closing the modal
   */
  closeModal(reason?: string): void {
    if (this.modalRef) this.modalRef.close(reason);
  }

  /**
   * Sets whether the confirm event should be emitted when modal closes.
   *
   * @remarks
   * Used internally to control whether closing the modal should trigger
   * the confirm event. Helps distinguish between confirm and cancel actions.
   *
   * @param flag - Whether to emit confirm event on modal close
   */
  setConfirm(flag: boolean): void {
    this.emitConfirm = flag;
  }

  /**
   * Cleans up resources when component is destroyed.
   *
   * @remarks
   * Ensures proper cleanup of observables and closes any open modal instances
   * to prevent memory leaks and unexpected behavior.
   */
  ngOnDestroy() {
    this.deregisterModalWindow();
    this.destroy$.next();
    this.destroy$.complete();
    this.closeModal();
  }

  /**
   * Handles keyboard events to manage modal focus trapping.
   *
   * @remarks
   * Prevents event propagation for all keys except Escape to maintain
   * proper focus management within the modal dialog.
   *
   * @param event - The keyboard event to handle
   */
  trapKeyEvent(event: KeyboardEvent) {
    if (getKeyCode(event) !== KEY_CODES.ESCAPE) {
      event.stopPropagation();
    }
  }

  private subscribeHeaderClose() {
    this.modalHeader?.close.pipe(takeUntil(merge(this.destroy$, this.headerChange$))).subscribe(() => {
      this.dismissModal();
    });
  }

  private subscribeIsOpen() {
    this._isOpen.pipe(filter(Boolean), takeUntil(this.destroy$)).subscribe(() => {
      this.setConfirm(true);

      this.modalBody?.setScrollable(this.modalOptions.scrollable);

      this.modalRef = this.modalService.open(this.modalContent, {
        ...this.modalOptions,
        ariaLabelledBy: this.ariaLabelledby,
        ariaDescribedBy: this.ariaDescribedby,
        role: (this.modalOptions.role ?? this.role) as 'alertdialog' | 'dialog',
      });
      this.registerModalWindow();
      afterNextRender({ write: () => this.markInitialFocusTarget() }, { injector: this.injector });
      this.modalRef.result.then(
        (result) => {
          if (this.emitConfirm) {
            this.confirm.emit(result);
          }
        },
        (reason) => this.cancel.emit(this.getDismissReason(reason)),
      );
    });

    this._isOpen
      .pipe(
        filter((value) => !value && !!this.modalRef),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.deregisterModalWindow();
        this.setConfirm(false);
        this.modalRef!.close();
      });
  }

  private registerModalWindow() {
    this.registeredModalWindow = this.document.querySelector('ngb-modal-window:last-of-type');
    if (this.registeredModalWindow) {
      this.modalFocusService.register(this.registeredModalWindow);
    }
  }

  /**
   * `ngbAutofocus` is the only way to tell `NgbModalWindow` which control to focus, so consumer
   * `cdkFocusInitial` is translated into it before the window chooses. `NgbModalWindow` focuses
   * during the `mixedReadWrite` render phase, and Angular always runs the `write` phase first,
   * so the attribute is in place regardless of which of the two registered its callback first.
   */
  private markInitialFocusTarget(): void {
    const modalWindow = this.registeredModalWindow;
    if (!modalWindow || modalWindow.querySelector(NGB_AUTOFOCUS_SELECTOR)) {
      return;
    }

    const target = modalWindow.querySelector(CDK_FOCUS_INITIAL_SELECTOR);
    if (target) {
      this.renderer.setAttribute(target, 'ngbAutofocus', '');
    }
  }

  private deregisterModalWindow() {
    if (this.registeredModalWindow) {
      this.modalFocusService.deregister(this.registeredModalWindow);
      this.registeredModalWindow = null;
    }
  }
}
